<template>
    <div :class="[textClass, extraClass]">
        {{ typedText }}<span v-if="showCursor" class="cursor-slot" :class="{ 'cursor-hidden': !isTypingDone, 'blinking-cursor': isTypingDone && isBlinking }">{{ cursor }}</span>
    </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue'
export default {
    props: {
        text: {
            type: String,
            required: true
        },
        speed: {
            type: Number,
            default: 30
        },
        showCursor: {
            type: Boolean,
            default: false
        },
        cursor: {
            type: String,
            default: '|'
        },
        stopBlinkAfter: {
            type: Number,
            default: 0
        },
        textClass: {
            type: String,
            default: 'text-indigo dark:text-amber font-semibold'
        },
        extraClass: {
            type: String,
            default: ''
        },
        pauseBetweenSentences: {
            type: Number,
            default: 0
        }
    },
    emits: ['done', 'progress'],
    setup(props, { emit }) {
        // Refs
        const typedText = ref('')
        const isTypingDone = ref(false)
        const isBlinking = ref(true)

        // Helpers
        let typingTimer = null
        let blinkStopTimer = null
        const clearTimers = () => {
            if (typingTimer) {
                clearTimeout(typingTimer)
                typingTimer = null
            }
            if (blinkStopTimer) {
                clearTimeout(blinkStopTimer)
                blinkStopTimer = null
            }
        }
        const typePrompt = () => {
            let i = 0
            const tick = () => {
                if (i < props.text.length) {
                    const ch = props.text[i]
                    typedText.value += ch
                    emit('progress')
                    i++
                    const nextCh = props.text[i]
                    // Pause longer after a sentence terminator (. ! ?) when followed by whitespace and more text.
                    // Skip the pause at end-of-text so @done fires without a stray delay.
                    const isSentenceBreak = /[.!?]/.test(ch) && nextCh !== undefined && /\s/.test(nextCh)
                    const delay = isSentenceBreak && props.pauseBetweenSentences > 0
                        ? props.pauseBetweenSentences
                        : props.speed
                    typingTimer = setTimeout(tick, delay)
                } else {
                    typingTimer = null
                    isTypingDone.value = true
                    emit('done')
                    if (props.stopBlinkAfter > 0) {
                        blinkStopTimer = setTimeout(() => {
                            isBlinking.value = false
                            blinkStopTimer = null
                        }, props.stopBlinkAfter)
                    }
                }
            }
            typingTimer = setTimeout(tick, props.speed)
        }
        const resetAndType = () => {
            clearTimers()
            typedText.value = ''
            isTypingDone.value = false
            isBlinking.value = true
            typePrompt()
        }

        // Watchers
        watch(() => [props.text, props.speed, props.stopBlinkAfter, props.pauseBetweenSentences], () => {
            resetAndType()
        })

        // Lifecycle
        onMounted(() => {
            typePrompt()
        })

        onUnmounted(() => {
            clearTimers()
        })

        return {
            typedText,
            isTypingDone,
            isBlinking
        }
    }
}
</script>

<style scoped>
@keyframes blink-cursor {
    0%, 49% { opacity: 1; }
    50%, 100% { opacity: 0; }
}
/* cursor-slot reserves the cursor character's width from the first paint so the line
   doesn't shift when typing finishes and the cursor becomes visible. */
.cursor-slot {
    margin-left: 2px;
}
.cursor-hidden {
    visibility: hidden;
}
.blinking-cursor {
    animation: blink-cursor 1s step-end infinite;
}
</style>
